import React, { useState } from 'react';
import { Dropzone } from '../Dropzone';
import type { EvidenceFormat, LinkAttachment } from '../../services/api';

/** A file staged for upload (not yet sent to documentsApi) - capturedAt defaults to the
 * file's own last-modified date but stays editable, since "when was this actually
 * captured" and "when did I last save the file" aren't always the same moment. */
export interface PendingFileAttachment {
  file: File;
  capturedAt: string; // ISO date (YYYY-MM-DD), editable
  /** First ~2000 chars of a small text file, read client-side for an inline preview -
   * never sent anywhere, display only. Only populated for config_export's text files. */
  textPreview?: string;
}

interface Props {
  evidenceFormat: EvidenceFormat;
  pendingFiles: PendingFileAttachment[];
  onPendingFilesChange: (files: PendingFileAttachment[]) => void;
  linkAttachments: LinkAttachment[];
  onLinkAttachmentsChange: (links: LinkAttachment[]) => void;
  disabled?: boolean;
}

const TEXT_PREVIEW_EXTENSIONS = ['.txt', '.csv', '.json', '.log', '.yaml', '.yml', '.conf', '.ini', '.xml'];
const TEXT_PREVIEW_MAX_SIZE = 256 * 1024; // 256KB - large enough for any real config export
const TEXT_PREVIEW_MAX_CHARS = 2000;

const isLikelyTextFile = (file: File): boolean =>
  TEXT_PREVIEW_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext)) && file.size <= TEXT_PREVIEW_MAX_SIZE;

const toDateInputValue = (lastModified: number): string => new Date(lastModified).toISOString().slice(0, 10);

const newLinkId = (): string => `link-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

export const AttachmentSection: React.FC<Props> = ({
  evidenceFormat, pendingFiles, onPendingFilesChange, linkAttachments, onLinkAttachmentsChange, disabled,
}) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [linkCapturedAt, setLinkCapturedAt] = useState(() => new Date().toISOString().slice(0, 10));

  const addFiles = async (files: File[]) => {
    const existing = new Set(pendingFiles.map((p) => p.file.name));
    const toAdd = files.filter((f) => !existing.has(f.name));
    const prepared: PendingFileAttachment[] = await Promise.all(
      toAdd.map(async (file) => {
        let textPreview: string | undefined;
        if (evidenceFormat === 'config_export' && isLikelyTextFile(file)) {
          try {
            const full = await file.text();
            textPreview = full.length > TEXT_PREVIEW_MAX_CHARS ? `${full.slice(0, TEXT_PREVIEW_MAX_CHARS)}…` : full;
          } catch {
            // Not actually readable as text (e.g. a binary file with a misleading extension) -
            // just skip the preview, the attachment itself is unaffected.
          }
        }
        return { file, capturedAt: toDateInputValue(file.lastModified), textPreview };
      }),
    );
    onPendingFilesChange([...pendingFiles, ...prepared]);
  };

  const removeFile = (name: string) => onPendingFilesChange(pendingFiles.filter((p) => p.file.name !== name));

  const updateFileCapturedAt = (name: string, capturedAt: string) =>
    onPendingFilesChange(pendingFiles.map((p) => (p.file.name === name ? { ...p, capturedAt } : p)));

  const addLink = () => {
    if (!linkUrl.trim()) return;
    const link: LinkAttachment = {
      id: newLinkId(), url: linkUrl.trim(), description: linkDescription.trim(), capturedAt: linkCapturedAt,
    };
    onLinkAttachmentsChange([...linkAttachments, link]);
    setLinkUrl('');
    setLinkDescription('');
    setLinkCapturedAt(new Date().toISOString().slice(0, 10));
  };

  const removeLink = (id: string) => onLinkAttachmentsChange(linkAttachments.filter((l) => l.id !== id));

  if (evidenceFormat === 'ticket_links') {
    return (
      <div>
        <div className="np-row2">
          <div className="np-field">
            <label>Ticket / link URL</label>
            <input className="np-input" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" disabled={disabled} />
          </div>
          <div className="np-field">
            <label>Captured at</label>
            <input type="date" className="np-input" value={linkCapturedAt} onChange={(e) => setLinkCapturedAt(e.target.value)} disabled={disabled} />
          </div>
        </div>
        <div className="np-field">
          <label>Short description</label>
          <input className="np-input" value={linkDescription} onChange={(e) => setLinkDescription(e.target.value)} placeholder="What this link shows…" disabled={disabled} />
        </div>
        <button type="button" className="np-btn np-btn-ghost np-btn-sm mb-2" onClick={addLink} disabled={disabled || !linkUrl.trim()}>
          <i className="ri-add-line"></i>Add link
        </button>
        {linkAttachments.length > 0 && (
          <div className="d-flex flex-column gap-1">
            {linkAttachments.map((l) => (
              <div key={l.id} className="d-flex align-items-center gap-2 np-sub">
                <i className="ri-link"></i>
                <a href={l.url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{l.description || l.url}</a>
                <span className="np-chip np-tone-neutral">{l.capturedAt}</span>
                {!disabled && <i className="ri-close-line" style={{ cursor: 'pointer' }} onClick={() => removeLink(l.id)}></i>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const isScreenshots = evidenceFormat === 'screenshots';
  return (
    <div>
      <Dropzone
        onFilesSelected={(files) => void addFiles(files)}
        accept={isScreenshots ? '.png,.jpg,.jpeg,.gif,.webp,.heic' : '.pdf,.png,.jpg,.jpeg,.xlsx,.docx,.doc,.xls,.csv,.txt,.json,.log,.yaml,.yml'}
        disabled={disabled}
        hint={isScreenshots ? 'PNG, JPG, GIF, WEBP, HEIC · max 25 MB each' : 'Config export or other file · max 25 MB each'}
      />
      {pendingFiles.length > 0 && (
        <div className="d-flex flex-column gap-2 mt-2">
          {pendingFiles.map((p) => (
            <div key={p.file.name} className="np-card" style={{ padding: '0.5rem 0.75rem' }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {isScreenshots && p.file.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(p.file)} alt={p.file.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                ) : (
                  <i className="ri-file-line ri-24px"></i>
                )}
                <span className="np-sub">{p.file.name}</span>
                <label className="np-sub mb-0 ms-auto" style={{ fontSize: '0.75rem' }}>Captured</label>
                <input
                  type="date" className="np-input" style={{ width: 150 }}
                  value={p.capturedAt} onChange={(e) => updateFileCapturedAt(p.file.name, e.target.value)}
                  disabled={disabled}
                />
                {!disabled && <i className="ri-close-line" style={{ cursor: 'pointer' }} onClick={() => removeFile(p.file.name)}></i>}
              </div>
              {p.textPreview && (
                <pre className="np-sub mt-2 mb-0" style={{ fontSize: '0.7rem', maxHeight: 120, overflow: 'auto', background: 'var(--np-surface-2)', padding: '0.5rem', borderRadius: 4 }}>
                  {p.textPreview}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
